import React, { useState } from 'react';
import {
  Box,
  TextField,
  Paper,
  Typography,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { savedItemsApi } from '../services/api';

/**
 * Reusable component for searching and selecting saved items
 */
function SavedItemsSearch({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Start typing to search for saved items...",
  label = "Search saved items",
  fullWidth = true,
  margin = "normal",
  size,
  sx = {}
}) {
  const [savedItems, setSavedItems] = useState([]);
  const [savedItemsLoading, setSavedItemsLoading] = useState(false);
  const [showSavedItems, setShowSavedItems] = useState(false);

  // Handle saved items search
  const handleSearch = async (searchQuery) => {
    onChange(searchQuery);
    
    if (searchQuery.trim().length === 0) {
      setSavedItems([]);
      setShowSavedItems(false);
      return;
    }
    
    if (searchQuery.trim().length < 2) {
      setShowSavedItems(false);
      return;
    }
    
    try {
      setSavedItemsLoading(true);
      const response = await savedItemsApi.search(searchQuery);
      setSavedItems(response.data);
      setShowSavedItems(true);
    } catch (error) {
      console.error('Error searching saved items:', error);
      setSavedItems([]);
      setShowSavedItems(false);
    } finally {
      setSavedItemsLoading(false);
    }
  };

  // Handle saved item selection
  const handleSavedItemSelect = (savedItem) => {
    onSelect(savedItem);
    setSavedItems([]);
    setShowSavedItems(false);
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <TextField
        fullWidth={fullWidth}
        label={label}
        value={value}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        margin={margin}
        size={size}
      />
      
      {/* Saved Items Results */}
      {showSavedItems && (
        <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000 }}>
          {savedItemsLoading ? (
            <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Searching...</Typography>
              </Box>
            </Paper>
          ) : savedItems.length > 0 ? (
            <Paper sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider' }}>
              {savedItems.map((savedItem) => (
                <Box
                  key={savedItem.item_id}
                  sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child': { borderBottom: 0 }
                  }}
                  onClick={() => handleSavedItemSelect(savedItem)}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {savedItem.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Units: {savedItem.units}
                  </Typography>
                </Box>
              ))}
            </Paper>
          ) : (
            <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                No saved items found matching "{value}"
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}

export default SavedItemsSearch;
